import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const basePath = ''; // Define the base path


interface AuthContextType {
  isAuthenticated: boolean;
  user: string | null;
  userRole: string | null;
  userEmail: string | null;
  loading: boolean;
  login: (provider?:string) => Promise<void>;
  logout: () => void;
  csrfToken: string | null; // <-- Add this line
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Utility to get CSRF token
export const getCsrfToken = async (): Promise<string> => {
  const response = await axios.get(`${API_BASE_URL}/auth/csrf-token`, {
    withCredentials: true,
  });
  return response.data.csrfToken;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState<string | null>(null); // Add CSRF token state

  const logout = useCallback(() => {
    clearSession();
    window.location.href = `${API_BASE_URL}/auth/logout`;
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // 1. Make an API request to check the current session
        const response = await axios.get(`${API_BASE_URL}/auth/session`, {
          withCredentials: true, // Ensure the session cookie is sent
        });

        // 2. Check if session exists and is valid
        if (response.status === 200) {
          const { user, userRole, userEmail } = response.data;

          // Verify the token explicitly to ensure it's valid
          const validToken = await verifyTokenWithBackend();
          if (validToken) {
            // Check if userRole is allowed
            if (userRole === "viewer" || userRole === "editor" || userRole === "subscriber") {
              setIsAuthenticated(true);
              setUser(user);
              setUserRole(userRole);
              setUserEmail(userEmail);
              // Fetch CSRF token only once on mount
              const csrf = await getCsrfToken();
              setCsrfToken(csrf);
              if (userRole === "subscriber") {
                if (window.location.pathname !== `${basePath}/subscription`) {
                  window.location.href = `${basePath}/subscription`; 
                }
              }
            } else {
              // Redirect to a 403 page or handle unauthorized access
              console.error("Unauthorized access: userRole not allowed"); 
              if (window.location.pathname !== `${basePath}/403`) {
                window.location.href = `${basePath}/403`; 
              }         
            }
          } else {
            clearSession();
          }
        } else {
          clearSession();
        }
      } catch (error) {
        console.error("Auth init error:", error);
        clearSession();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Periodically verify the token (every 5 minutes)
    const intervalId = setInterval(async () => {
      const valid = await verifyTokenWithBackend();
      if (!valid) logout();
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [logout]);

  const verifyTokenWithBackend = async (): Promise<boolean> => {
    try {
      const res = await axios.get(`${API_BASE_URL}/auth/verify-token`, {
        withCredentials: true,
      });
      return res.status === 200;
    } catch (error) {
      return false;
    }
  };

  const login = async (provider?:string ): Promise<void> => {
    try {
      setLoading(true);
      if (provider === 'msal') {
      window.location.href = `${API_BASE_URL}/auth/login?provider=msal`;
      }
      else  {
        window.location.href = `${API_BASE_URL}/auth/login?provider=cloud-idp`;
      }
    } catch (error) {
      console.error('Login error:', error);
    }
    finally {
      setLoading(false);
    }
  };

  const clearSession = () => {
    setIsAuthenticated(false);
    setUser(null);
    setUserRole(null);
    setUserEmail(null);
    Cookies.remove('accessToken');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, userRole, userEmail, loading, login, logout, csrfToken }}>
      {children}
    </AuthContext.Provider>
  );
};
